/**
 * Speak Easy Co — site-wide conversion tracking.
 *
 * Loaded once from layout/theme.liquid, so it runs on EVERY page.
 *
 * It sends exactly three things:
 *
 *   1. Meta "Contact"        — when a visitor clicks ANY business phone
 *                              link, on any page (footer link, "CALL
 *                              813-403-8285" buttons, "CALL NOW" button).
 *   2. GA4  "click_to_call"  — the same click, sent to GA4 only, for
 *                              reporting. Deliberately NOT a Google Ads
 *                              conversion (see note below).
 *   3. Meta "Lead"           — when Shopify confirms a successful
 *                              consultation submission.
 *
 * DO NOT ADD A GOOGLE LEAD EVENT HERE.
 * generate_lead is owned by the GA4 "Create event" rule
 * (form_submit + form_id = contact_form). Sending one from here as well
 * would double-count every lead in Google Ads.
 *
 * DO NOT MARK click_to_call AS A KEY EVENT IN GA4.
 * The Google Ads website-call conversion already counts real calls of
 * 60+ seconds via the forwarding number. Counting clicks as a conversion
 * too would count the same person twice on a much weaker signal.
 *
 * The base Google tag and Meta pixel are supplied by the Shopify Google &
 * YouTube and Facebook channels. This file never installs either one; it
 * only calls them if they are already present, and fails silently if they
 * are not (ad blockers, consent tooling, offline).
 *
 * Safety: every tracking path is wrapped so that a tracking failure can
 * never throw inside a click handler and never blocks the tel: link.
 */
(() => {
  'use strict';

  /* Idempotent: a second load re-scans instead of binding twice. */
  if (window.SEConversionTracking) {
    try {
      window.SEConversionTracking.scan();
    } catch (_error) {
      /* never throw */
    }
    return;
  }

  const GA4_MEASUREMENT_ID = 'G-6C5YYX2SLN';
  const CALL_CLICK_EVENT_NAME = 'click_to_call';

  const LEAD_DEDUPLICATION_WINDOW_MS = 5 * 60 * 1000;
  const CALL_DEDUPLICATION_WINDOW_MS = 1500;
  const MAX_PIXEL_WAIT_ATTEMPTS = 120;
  const PIXEL_WAIT_INTERVAL_MS = 500;

  /* Store-pickup location numbers are not the business line. */
  const EXCLUDED_PHONE_CONTAINER = 'pickup-availability';

  let lastCallHref = '';
  let lastCallTime = 0;

  function sendMetaEvent(eventName, parameters) {
    try {
      if (typeof window.fbq !== 'function') {
        return false;
      }
      window.fbq('track', eventName, parameters);
      return true;
    } catch (_error) {
      return false;
    }
  }

  function sendGoogleEvent(eventName, parameters) {
    try {
      if (typeof window.gtag !== 'function') {
        return false;
      }
      const payload = { send_to: GA4_MEASUREMENT_ID };
      for (const key in parameters) {
        if (Object.prototype.hasOwnProperty.call(parameters, key)) {
          payload[key] = parameters[key];
        }
      }
      window.gtag('event', eventName, payload);
      return true;
    } catch (_error) {
      return false;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Consultation lead (Meta only)                                       */
  /* ------------------------------------------------------------------ */

  function getLeadStorageKey(marker) {
    let sectionId = 'contact';
    try {
      const section = marker.closest('[data-se-meta-contact-section]');
      if (section && section.dataset && section.dataset.sectionId) {
        sectionId = section.dataset.sectionId;
      }
    } catch (_error) {
      /* fall back to the default key */
    }
    return `se-meta-lead:${window.location.pathname}:${sectionId}`;
  }

  function wasLeadRecentlyTracked(storageKey) {
    try {
      const lastTrackedAt = Number(window.sessionStorage.getItem(storageKey) || 0);
      return Date.now() - lastTrackedAt < LEAD_DEDUPLICATION_WINDOW_MS;
    } catch (_error) {
      return false;
    }
  }

  function rememberTrackedLead(storageKey) {
    try {
      window.sessionStorage.setItem(storageKey, String(Date.now()));
    } catch (_error) {
      /* Tracking still succeeds when session storage is unavailable. */
    }
  }

  function trackSuccessfulInquiry(marker, attempt) {
    try {
      const currentAttempt = attempt || 0;

      if (marker.dataset.seMetaLeadTracked === 'true') {
        return;
      }

      const storageKey = getLeadStorageKey(marker);

      if (wasLeadRecentlyTracked(storageKey)) {
        marker.dataset.seMetaLeadTracked = 'true';
        return;
      }

      const sent = sendMetaEvent('Lead', {
        content_name: 'Consultation Inquiry',
        content_category: 'Consultation'
      });

      if (sent) {
        marker.dataset.seMetaLeadTracked = 'true';
        rememberTrackedLead(storageKey);
        return;
      }

      if (currentAttempt < MAX_PIXEL_WAIT_ATTEMPTS) {
        window.setTimeout(function () {
          trackSuccessfulInquiry(marker, currentAttempt + 1);
        }, PIXEL_WAIT_INTERVAL_MS);
      }
    } catch (_error) {
      /* never throw */
    }
  }

  function scan() {
    try {
      const markers = document.querySelectorAll('[data-se-meta-lead-success]');
      for (let i = 0; i < markers.length; i++) {
        trackSuccessfulInquiry(markers[i], 0);
      }
    } catch (_error) {
      /* never throw */
    }
  }

  /* ------------------------------------------------------------------ */
  /* Phone-link clicks (Meta + GA4), site-wide                           */
  /* ------------------------------------------------------------------ */

  /**
   * This theme's button component renders its label twice (for the hover
   * transition), so textContent comes back doubled, e.g.
   * "CALL 813-403-8285CALL 813-403-8285". Collapse that back to one copy
   * so GA4 reporting stays readable. Normal labels are returned unchanged.
   */
  function dedupeDoubledLabel(rawText) {
    const text = (rawText || '').replace(/\s+/g, ' ').trim();
    const length = text.length;

    if (length >= 2 && length % 2 === 0) {
      const half = length / 2;
      if (text.slice(0, half) === text.slice(half)) {
        return text.slice(0, half);
      }
    }

    /* Same doubling, separated by a single space. */
    if (length >= 3) {
      const middle = Math.floor((length - 1) / 2);
      if (text.charAt(middle) === ' ') {
        const first = text.slice(0, middle);
        if (first && first === text.slice(middle + 1)) {
          return first;
        }
      }
    }

    return text;
  }

  function handleDocumentClick(event) {
    try {
      const clickedElement = event.target instanceof Element ? event.target : null;
      if (!clickedElement) {
        return;
      }

      const callLink = clickedElement.closest('a[href^="tel:"]');
      if (!callLink) {
        return;
      }

      /* Ignore store-pickup location numbers. */
      if (callLink.closest(EXCLUDED_PHONE_CONTAINER)) {
        return;
      }

      const now = Date.now();
      const href = callLink.getAttribute('href') || '';

      if (href === lastCallHref && now - lastCallTime < CALL_DEDUPLICATION_WINDOW_MS) {
        return;
      }

      lastCallHref = href;
      lastCallTime = now;

      /* Preserved from the previous implementation so Meta reporting
         stays continuous. */
      sendMetaEvent('Contact', {
        content_name: 'Call Now',
        content_category: 'Phone'
      });

      let linkText = '';
      try {
        linkText = dedupeDoubledLabel(callLink.textContent).slice(0, 100);
      } catch (_error) {
        linkText = '';
      }

      /* GA4 only. Never sent to Google Ads — see header note. */
      sendGoogleEvent(CALL_CLICK_EVENT_NAME, {
        link_url: href,
        link_text: linkText
      });
    } catch (_error) {
      /* never throw — must not interfere with the tel: navigation */
    }
  }

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', handleDocumentClick, { capture: true });
  document.addEventListener('shopify:section:load', scan);

  window.SEConversionTracking = { scan };

  /* Safety interlock: the retired assets/meta-conversion-tracking.js
     bails out when it sees this global, so it can never double-bind even
     if something loads it. */
  window.SEConsultationMetaTracking = { scan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }
})();
